// ============================================================================
// PermissionsService - Security Key Checks & Menu Building
// ============================================================================
// Replaces V1's svc-permissions.js (Perms service). Reads numeric security keys
// from userInfo.security["N"] to set boolean permission flags (catalogView,
// orderCreate, etc.). Builds the left nav menu items based on granted permissions.
// ============================================================================

import { Injectable } from '@angular/core';
import { SessionService } from './session.service';

export interface MenuItem {
  url: string;
  text: string;
  icon: string;
}

/**
 * Replicates V1's Perms service (svc-permissions.js).
 * Security keys are numeric values in userInfo.security["N"].
 * Higher values = more access.
 */
@Injectable()
export class PermissionsService {
  // Permission flags
  feedView = false;
  invoiceView = false;
  creditAmtView = false;
  catalogView = false;
  orderView = false;
  orderCreate = false;
  rmaView = false;
  rmaCreate = false;
  chat = false;
  quoteView = false;
  fileView = false;
  fileCreate = false;
  fileDelete = false;

  menuItems: MenuItem[] = [];

  constructor(private session: SessionService) {}

  /**
   * Calculate permissions from user security keys.
   * Call after initialDataLoad completes.
   */
  calculate(): void {
    const user = this.session.userInfo;
    if (!user?.security) return;

    const sec = user.security;
    const company = this.session.companyInfo;

    this.feedView = parseInt(sec['1']) >= 10 && company?.datafeedenabled == 1;
    this.invoiceView = parseInt(sec['3']) >= 5;
    this.creditAmtView = parseInt(sec['2']) >= 10 && (company?.credit > 0);
    this.catalogView = parseInt(sec['4']) >= 5;
    this.orderView = parseInt(sec['6']) >= 0;
    this.orderCreate = parseInt(sec['5']) >= 10;
    this.rmaView = parseInt(sec['8']) >= 5 || parseInt(sec['3']) >= 5;
    this.rmaCreate = parseInt(sec['7']) >= 10;
    this.chat = parseInt(sec['9']) >= 10;
    this.quoteView = parseInt(sec['11']) >= 10;
    this.fileView = parseInt(sec['12']) >= 3;
    this.fileCreate = parseInt(sec['12']) >= 5 && parseInt(sec['12']) < 10;
    this.fileDelete = parseInt(sec['12']) >= 10;

    this.buildMenu();
  }

  private buildMenu(): void {
    const items: MenuItem[] = [
      { url: '/dash', text: 'Dashboard', icon: 'pi-home' }
    ];

    if (this.catalogView) {
      items.push({ url: '/quotes', text: 'Quotes', icon: 'pi-comment' });
      items.push({ url: '/products', text: 'Products', icon: 'pi-cog' });
    }
    if (this.orderView) {
      items.push({ url: '/orders', text: 'Orders', icon: 'pi-truck' });
    }
    if (this.rmaView) {
      items.push({ url: '/rmas', text: 'RMAs', icon: 'pi-replay' });
    }
    if (this.quoteView && !this.catalogView) {
      items.push({ url: '/quotes', text: 'Quotes', icon: 'pi-comment' });
    }
    if (this.invoiceView) {
      items.push({ url: '/balance', text: 'Balance', icon: 'pi-dollar' });
    }
    if (this.fileView) {
      items.push({ url: '/files', text: 'File\nManagement', icon: 'pi-file' });
    }
    items.push({ url: '/favorites', text: 'Favorites', icon: 'pi-heart' });
    items.push({ url: '/settings', text: 'Settings', icon: 'pi-wrench' });

    this.menuItems = items;
  }
}
